import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrManager } from '@niceltd/cxone-components/toastr';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { SystemTemplateModel } from 'src/shared/models/system-template.models';
import { Status } from '../../../../shared/models/dashboard.model';
import { CXOneMultiSelectChanged, CXOneMultiSelectOption } from '../../../reusables/multi-select/multi-select.models';
import { DashboardService } from '../../dashboard.service';
import { SystemTemplateService } from '../../system-templates/system-template.service';

@Component({
    selector: 'cv-create-new-report-modal',
    templateUrl: './create-new-report-modal.component.html',
    styleUrls: ['./create-new-report-modal.component.scss']
})
export class CreateNewReportModalComponent implements OnInit {
    @Input() type: string; // Is used to know which operation is being executed.
    @Input() editData; // Data coming from manage-reports.component.ts, function editDetailsClicked().

    invalidCharsRegex = new RegExp(`^([^\\\\\/\!\+\<\>\?\#\&\,\%])*$`);
    invalidDescriptionCharsRegex = new RegExp(`^([^\\\\\/\!\+\<\>\?\#\&\%])*$`);
    invalidCharsNoSpacesRegex = new RegExp(`^([^\\\\\/\!\+\<\>\?\#\&\,\% \t])*$`);
    isCXOne = false;
    hidePlural = false;
    isPatternValid = true;
    isNameValid = true;
    maxChars = 50;
    isLoading = false;
    isSaving = false;
    hasWriteAccess = true;
    data: SystemTemplateModel = {
        key: "",
        name: "",
        description: "",
        personas: [1001], // CXone Agent Persona. Must match the value in the controller
        group: "",
        integrations: [20], // CXone Core Dashboards Integration. Must match the value in the controller
        selectedDomains: "",
        userType: "",
        status: 0,
        lastModifiedDate: "",
        lastModifiedBy: "",
        permissionName: "",
    };

    domainsList: CXOneMultiSelectOption[] = []; // Master list of domains available.
    selectedDomainsList: CXOneMultiSelectOption[] = []; // List of which domains are currently selected.
    personaList: CXOneMultiSelectOption[] = []; // Master list of Personass available.
    selectedPersonaList: CXOneMultiSelectOption[] = []; // List of which Personas are currently selected.

    constructor(
        public toastrManager: ToastrManager,
        public systemTemplateService: SystemTemplateService,
        public dashboardService: DashboardService,
        public activeModal: NgbActiveModal,
        public cvLocalizationService: CVLocalizationService,
    ) { }

    async loadLocalizations() { //moving this to ngOnInit breaks the cxone multiselect dropdown so it doesnt populate
        await this.cvLocalizationService.GetSection("Dashboard_Input");
        await this.cvLocalizationService.GetSection("CXOne_Modal");
    }

    ngOnInit(): void {
        this.loadLocalizations();
        this.characterLimit(this.data?.name);
        this.personaList.push(
            { id: "0", name: "Agent", selected: false, sort: "" },
            { id: "1", name: "Supervisor", selected: false, sort: "" },
            { id: "2", name: "Quality Manager", selected: false, sort: "" },
            { id: "3", name: "Workforce Manager", selected: false, sort: "" },
            { id: "4", name: "Operation Manager", selected: false, sort: "" },
            { id: "5", name: "Executive", selected: false, sort: "" },
            { id: "6", name: "Auditor", selected: false, sort: "" },
            { id: "7", name: "Billing Manager", selected: false, sort: "" },
            { id: "8", name: "Analyst", selected: false, sort: "" },
        );

        this.domainsList.push( // Hardcoded domains list
            { id: "0", name: "ACD", selected: false, sort: "" },
            { id: "1", name: "Omni Channel", selected: false, sort: "" },
            { id: "2", name: "General", selected: false, sort: "" },
            { id: "3", name: "Recording", selected: false, sort: "" },
            { id: "4", name: "QM", selected: false, sort: "" },
            { id: "5", name: "WFM", selected: false, sort: "" },
            { id: "6", name: "Analytics", selected: false, sort: "" },
            { id: "7", name: "Coaching", selected: false, sort: "" },
            { id: "8", name: "DFO", selected: false, sort: "" },
            { id: "9", name: "CXone Guide", selected: false, sort: "" });

        if (this.type == "Edit") {
            // Setting values.
            this.data.key = this.editData.SystemTemplateKey;
            this.data.name = this.editData.SystemTemplateName;
            this.data.description = this.editData.SystemTemplateDesc;
            this.data.personas = this.editData.Personas;
            this.data.group = this.editData.SystemTemplateGroup;
            this.data.integrations = this.editData.Integrations;
            this.data.selectedDomains = this.editData.SelectedDomains;
            this.data.userType = this.editData.UserType;
            this.data.status = this.editData.Status;
            this.data.lastModifiedDate = this.editData.LastModifiedDate;
            this.data.lastModifiedBy = this.editData.LastModifiedBy;
            this.data.permissionName = this.editData.PermissionName;

            // Getting the selected domains.
            const tempDomainsList = this.data.selectedDomains.split(', '); // Dividing the SelectedDomains string into an array of names.
            this.domainsList.forEach((domain) => { // Filling selectedDomainList based on the names in the tempDomainsList array.
                tempDomainsList.forEach((tempDomain) => {
                    if (domain.name === tempDomain) {
                        domain.selected = true;
                        this.selectedDomainsList.push(domain);
                    }
                });
            });

            // Getting the selected personas.
            const tempPersonas = this.data.userType.split(', ');
            this.personaList.forEach((persona) => {
                tempPersonas.forEach((tempPersona) => {
                    if (persona.name === tempPersona) {
                        persona.selected = true;
                        this.selectedPersonaList.push(persona);
                    }
                });
            });
        }
    }

    cancel() {
        this.activeModal.dismiss();
    }

    async save() {
        if(this.isSaving) {
            return;
        }

        this.isSaving = true;

        try {
            if (this.type === "Create") {
                const savedTemplateKey = await this.systemTemplateService.saveNewCxOneSystemTemplate(this.data);
                this.activeModal.close(savedTemplateKey);
            }
            if (this.type === "Edit") {
                if (this.data.status === Status.Published) {
                    this.data.status = Status.Published_Draft;
                } else if (this.data.status === Status.Unpublished) {
                    this.data.status = Status.Draft;
                }

                const response = await this.systemTemplateService.updateCxOneSystemTemplate(this.data);
                if (this.data.lastModifiedBy != response.data[0]) {
                    this.data.lastModifiedBy = response.data[0];
                }
                if (this.data.lastModifiedDate.substring(0, 19) != response.data[1].substring(0, 19)) {
                    this.data.lastModifiedDate = response.data[1];
                }

                this.activeModal.close(this.data);
            }
        } catch (ex) {
            this.toastrManager.showToaster("error", ex.data);
        }

        this.isSaving = false;
    }

    isStringEmpty(input: string) {
        return !input || !input.trim();
    }

    characterLimit(value: string) {
        const editName = value.trim();
        if (!this.invalidCharsRegex.test(editName.toLocaleLowerCase()) && !editName.toLocaleLowerCase().match(this.invalidCharsRegex)) {
            this.isPatternValid = false;
        } else {
            this.isPatternValid = true;
        }
    }

    getTextLimitMessage(value: string) {
        const textLimit = this.maxChars - value.length;
        if (textLimit === 1)
            return this.cvLocalizationService.Format(
                this.cvLocalizationService.getCachedText("Dashboard_Input", "Character_Limit_Singular", "{0} character remaining"), textLimit
            );
        else
            return this.cvLocalizationService.Format(
                this.cvLocalizationService.getCachedText("Dashboard_Input", "Character_Limit", "{0} characters remaining"), textLimit
            );
    }

    onDomainsSelectionChanged(change: CXOneMultiSelectChanged) {
        this.data.selectedDomains = "";
        change.selected.forEach((domain) => {
            this.data.selectedDomains = this.data.selectedDomains + domain.name + ", ";
        });
        this.data.selectedDomains = this.data.selectedDomains.slice(0, -2);
    }

    onPersonaSelectionChanged(change: CXOneMultiSelectChanged) {
        this.data.userType = "";
        change.selected.forEach((userType) => {
            this.data.userType = this.data.userType + userType.name + ", ";
        });
        this.data.userType = this.data.userType.slice(0, -2);
    }
}
